<template>
  <div>
    <nav>
      <div>
        <v-app-bar app color="white">
          <v-row
            v-if="!isMobileHeader"
            no-gutters
            class="desktop-header-row align-center flex-nowrap"
          >
            <v-col cols="auto" class="desktop-logo-col align-center d-flex">
              <router-link :to="'/'">
                <img
                  id="logo-image"
                  :src="require('@/assets/logo-purple.svg')"
                  class="logo desktop-logo"
                />
              </router-link>
            </v-col>
            <v-col class="desktop-search-col align-center d-flex justify-end">
              <InputAutocompleteSearch
                v-model="search"
                class="landing-header-search"
                placeholder="Search facilities, equipment"
                label=""
                :is-combobox="true"
                prepend-inner-icon="mdi-magnify"
                outlined
                clearable
                :hide-details="true"
                data-cy="search-generic"
                :item-value="'id'"
                :item-text="'name'"
                :url="'/api/v1/resources/by-name?name='"
                @click:prepend-inner="triggerSearch"
                @keyup.native.enter="triggerSearch"
                @update:search-input="searchChange"
                @change="triggerSearch"
              >
                <template v-slot:append>
                  <v-btn icon small @mousedown.native.stop @click.stop="openFilterDrawer">
                    <v-icon color="primary200">
                      $vuetify.icons.filter
                    </v-icon>
                  </v-btn>
                </template>
              </InputAutocompleteSearch>
            </v-col>
            <v-col cols="auto" class="desktop-menu-col align-center d-flex justify-center">
              <div class="header-menu-container">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text class="header-menu-button" v-bind="attrs" v-on="on">
                      <span
                        class="header-menu-label neutral--text"
                        :class="{ 'header-menu-label--active': isAboutRouteActive }"
                        >About</span
                      >
                      <v-icon right>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list flat class="menu-list">
                    <v-list-item
                      v-for="(item, index) in aboutItems"
                      :key="index"
                      class="menu-item"
                      @click="handleAboutItemClick(item)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-menu v-model="catalogueMenuOpen" offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text class="header-menu-button" v-bind="attrs" v-on="on">
                      <span
                        class="header-menu-label neutral--text"
                        :class="{ 'header-menu-label--active': isCatalogueRouteActive }"
                        >Catalogue</span
                      >
                      <v-icon right>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list flat class="menu-list">
                    <v-list-item
                      v-for="(item, index) in marketPlaceItems"
                      :key="index"
                      class="menu-item"
                      @click="handleMarketPlaceItemClick(item)"
                    >
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                    <template v-if="patentsMenuOpen">
                      <v-list-item
                        v-for="(item, index) in intellectualPropertiesItems"
                        :key="`intellectual-property-${index}`"
                        class="pl-8"
                        @click="handleMarketPlaceItemClick(item)"
                      >
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-menu>
              </div>
            </v-col>
            <v-col cols="auto" class="desktop-auth-col d-flex align-center justify-end">
              <v-btn
                v-if="!$store.getters.roles"
                x-large
                dark
                class="sign-in-button"
                color="#9D57F4"
                @click="login()"
              >
                <v-icon left>mdi-account-circle</v-icon>
                <span class="sign-in-button__text">Sign in</span>
              </v-btn>
              <v-btn
                v-else
                x-large
                dark
                link
                class="sign-in-button"
                :to="{ name: 'Dashboard' }"
                color="#9D57F4"
              >
                <span class="sign-in-button__text">Dashboard</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-else no-gutters class="mobile-header-row align-center flex-nowrap">
            <v-col cols="auto" class="d-flex align-center">
              <router-link :to="'/'" aria-label="Go to home page">
                <img
                  :src="require('@/assets/logo-purple.svg')"
                  class="logo mobile-logo"
                  alt="Research Hub"
                />
              </router-link>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="d-flex align-center">
              <v-menu
                v-model="mobileSearchOpen"
                offset-y
                left
                :close-on-content-click="false"
                min-width="calc(100vw - 24px)"
                max-width="calc(100vw - 24px)"
                transition="slide-y-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon aria-label="Open search" v-bind="attrs" v-on="on">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <v-card class="mobile-search-card pa-3">
                  <InputAutocompleteSearch
                    v-model="search"
                    class="landing-header-search"
                    placeholder="Search facilities, equipment"
                    label=""
                    :is-combobox="true"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    clearable
                    autofocus
                    :hide-details="true"
                    data-cy="search-generic-mobile"
                    :item-value="'id'"
                    :item-text="'name'"
                    :url="'/api/v1/resources/by-name?name='"
                    @click:prepend-inner="triggerSearch"
                    @keyup.native.enter="triggerSearch"
                    @update:search-input="searchChange"
                    @change="triggerSearch"
                  >
                    <template v-slot:append>
                      <v-btn icon small @mousedown.native.stop @click.stop="openFilterDrawer">
                        <v-icon color="primary200">$vuetify.icons.filter</v-icon>
                      </v-btn>
                    </template>
                  </InputAutocompleteSearch>
                </v-card>
              </v-menu>
              <v-btn icon aria-label="Open navigation menu" @click="openMobileDrawer">
                <v-icon>mdi-menu</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-app-bar>
      </div>
      <v-navigation-drawer
        v-if="isMobileHeader"
        v-model="mobileDrawerOpen"
        app
        dark
        right
        fixed
        temporary
        width="320"
        class="mobile-navigation-drawer black"
      >
        <v-list nav dark class="pa-3">
          <v-list-item class="px-0">
            <v-list-item-content>
              <v-list-item-title class="mobile-drawer-heading">
                <img
                  id="logo-image"
                  :src="require('@/assets/logo-white.svg')"
                  class="logo desktop-logoo"
                />
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                color="white"
                icon
                aria-label="Close navigation menu"
                @click="mobileDrawerOpen = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="mb-2"></v-divider>

          <v-list-group v-model="mobileAboutGroupOpen" no-action>
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>
                  <span
                    class="mobile-drawer-label text-bold"
                    :class="{ 'mobile-drawer-label--active': isAboutRouteActive }"
                    style="color: white;"
                  >
                    About
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-slot:appendIcon>
              <v-icon class="mobile-drawer-toggle-icon">
                {{ mobileAboutGroupOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </template>
            <v-list-item
              v-for="(item, index) in aboutItems"
              :key="`mobile-about-${index}`"
              link
              @click="handleAboutItemClick(item)"
            >
              <v-list-item-content>
                <v-list-item-title style="color: white;" class="text-bold">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>

          <v-list-group v-model="mobileCatalogueGroupOpen" no-action>
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>
                  <span
                    class="mobile-drawer-label text-bold"
                    :class="{ 'mobile-drawer-label--active': isCatalogueRouteActive }"
                    style="color: white;"
                  >
                    Catalogue
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-slot:appendIcon>
              <v-icon class="mobile-drawer-toggle-icon">
                {{ mobileCatalogueGroupOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </template>
            <v-list-item
              v-for="(item, index) in marketPlaceItems"
              :key="`mobile-catalogue-${index}`"
              link
              @click="handleMarketPlaceItemClick(item)"
            >
              <v-list-item-content>
                <v-list-item-title style="color: white;" class="text-bold">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <template v-if="patentsMenuOpen">
              <v-list-item
                v-for="(item, index) in intellectualPropertiesItems"
                :key="`mobile-intellectual-property-${index}`"
                link
                class="pl-12"
                @click="handleMarketPlaceItemClick(item)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list-group>

          <v-list-item class="mt-4 px-0">
            <v-btn
              v-if="!$store.getters.roles"
              block
              x-large
              dark
              color="#9D57F4"
              class="mobile-auth-button"
              @click="handleMobileLogin"
              style="border-radius: 60px;"
            >
              <v-icon left>mdi-account-circle</v-icon>
              <span class="mobile-auth-button__text">Sign in</span>
            </v-btn>
            <v-btn
              v-else
              block
              x-large
              dark
              link
              color="#9D57F4"
              class="mobile-auth-button"
              :to="{ name: 'Dashboard' }"
              @click="mobileDrawerOpen = false"
              style="border-radius: 60px;"
            >
              <span class="mobile-auth-button__text">Dashboard</span>
            </v-btn>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>
      <Filtering></Filtering>
    </nav>
  </div>
</template>

<script>
import util from '@/mixins/util';
import InputAutocompleteSearch from '../inputs/InputAutocompleteSearch.vue';
import EventBus from '@/helpers/event-bus';
import Filtering from '../Filtering.vue';

export default {
  name: 'HeaderLandingNew',
  components: { InputAutocompleteSearch, Filtering },
  mixins: [util],
  data() {
    return {
      search: null,
      mobileSearchOpen: false,
      mobileDrawerOpen: false,
      mobileAboutGroupOpen: false,
      mobileCatalogueGroupOpen: false,
      catalogueMenuOpen: false,
      patentsMenuOpen: false,
      aboutItems: [{ title: 'About Us' }, { title: 'How it Works' }, { title: 'Tutorials' }],
      marketPlaceItems: [
        { title: 'Hosting Institutions' },
        { title: 'Providers' },
        { title: 'Services' },
        { title: 'Equipment' },
        { title: 'Intellectual Properties' },
      ],
      intellectualPropertiesItems: [{ title: 'Patents' }],
    };
  },
  watch: {
    isMobileHeader(isMobile) {
      if (!isMobile) {
        this.mobileSearchOpen = false;
        this.mobileDrawerOpen = false;
      }
    },
  },
  computed: {
    isMobileHeader() {
      return this.$vuetify.breakpoint.width <= 768;
    },
    isAboutRouteActive() {
      const aboutRoutes = ['/about', '/how-it-works', '/tutorials'];

      return aboutRoutes.some(
        route => this.$route.path === route || this.$route.path.startsWith(`${route}/`),
      );
    },
    isCatalogueRouteActive() {
      return /^\/(marketplace|public-catalogue-marketplace)(\/|$)/.test(this.$route.path);
    },
  },
  methods: {
    triggerSearch(value) {
      if (value == null) return;

      let searchText = this.search?.name || this.search;
      searchText = String(searchText || '').trim();

      if (!searchText) return;

      this.mobileSearchOpen = false;

      if (this.$store.getters.roles != null) {
        this.$router.push({
          name: 'SearchMarketplace',
          query: { text: searchText },
        });
      } else {
        this.$router.push({
          name: 'PublicSearchMarketplace',
          query: { text: searchText },
        });
      }
    },
    searchChange(value) {
      this.search = value;
    },
    handleAboutItemClick(item) {
      const routes = {
        'About Us': '/about',
        'How it Works': '/how-it-works',
        Tutorials: '/tutorials',
      };
      const route = routes[item.title];

      if (route && this.$route.path !== route) {
        this.$router.push(route);
      }

      this.mobileDrawerOpen = false;
    },
    handleMarketPlaceItemClick(item) {
      if (item.title === 'Intellectual Properties') {
        this.patentsMenuOpen = !this.patentsMenuOpen;
        return;
      }

      const routeConfig = {
        'Hosting Institutions': {
          slug: 'hosting-institutions',
          selectedCatalogue: 'Browse the Catalogue of Hosting Institutions',
        },
        Providers: {
          slug: 'providers',
          selectedCatalogue: 'Browse the Catalogue of Providers',
          viewAllKey: 'marketplaceProvidersViewAll',
        },
        Services: {
          slug: 'services',
          selectedCatalogue: 'Browse the Catalogue of Services',
          viewAllKey: 'marketplaceServicesViewAll',
        },
        Equipment: {
          slug: 'equipment',
          selectedCatalogue: 'Browse the Catalogue of Equipment',
          viewAllKey: 'marketplaceEquipmentViewAll',
        },
        Patents: {
          slug: 'patents',
          selectedCatalogue: 'Browse the Catalogue of Patents',
          viewAllKey: 'marketplacePatentsViewAll',
        },
      };
      const config = routeConfig[item.title];

      if (!config) return;

      const isAuthenticated = this.$store.getters.roles != null;
      const basePath = isAuthenticated ? '/marketplace' : '/public-catalogue-marketplace';
      const query = { selectedCatalogue: config.selectedCatalogue };

      if (config.viewAllKey) {
        query[config.viewAllKey] = true;
      }

      this.catalogueMenuOpen = false;
      this.patentsMenuOpen = false;
      this.mobileDrawerOpen = false;
      this.$router.push({
        path: `${basePath}/${config.slug}`,
        query,
      });
    },
    openFilterDrawer() {
      this.mobileSearchOpen = false;
      this.mobileDrawerOpen = false;
      EventBus.$emit('filtering', true);
    },
    openMobileDrawer() {
      this.mobileSearchOpen = false;
      this.mobileDrawerOpen = true;
    },
    handleMobileLogin() {
      this.mobileDrawerOpen = false;
      this.login();
    },
  },
};
</script>

<style lang="scss" scoped src="../../../styles/header-landing-new.scss"></style>
